import { ComponentProps, FC, ReactNode, useEffect, useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { Check } from 'lucide-react';

import { cn } from '../lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  UIButton,
  UILabel
} from '../ui';

export type InputSelectDropdownOption = {
  dataTestId?: string;
  value: string | number;
  content?: string | number;
  label: string;
  rightIcon?: ReactNode;
  leftIcon?: ReactNode;
  disabled?: boolean;
  image?: string;
  shouldHighlightSelectedOption?: boolean;
  hideLabel?: boolean;
};

export type InputSelectDropdownProps = ComponentProps<typeof Popover> & {
  options: InputSelectDropdownOption[];
  label?: string;
  searchPlaceholder?: string;
  noSearchResultsText?: string;
  defaultSelected?: InputSelectDropdownOption;
  isHorizontal?: boolean;
  description?: string;
  className?: string;
  iconClassName?: string;
  contentClassName?: string;
  disabled?: boolean;
  dropdownAlignment?: 'start' | 'center' | 'end';
  dropdownSide?: 'top' | 'right' | 'bottom' | 'left';
  showCheckMarkOnSelect?: boolean;
  shouldHighlightSelectedOption?: boolean;
  shouldCapitalizeOptions?: boolean;
  onOptionChange?: (value: string) => void;
  isRequired?: boolean;
  placeholder?: string;
  id?: string;
  dataTestId?: string;
  buttonDataTestId?: string;
  listDataTestId?: string;
  buttonClassName?: string;
  descriptionDataTestId?: string;
  state?: 'default' | 'error' | 'success';
  onClear?: () => void;
  clearIconPosition?: 'left' | 'right';
  emptyStateRenderer?: (searchValue: string, clearSearch: () => void, closeDropdown: () => void) => ReactNode;
  emptyStateClassName?: string;
  onSearchKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>, searchValue: string, clearSearch: () => void, closeDropdown: () => void) => void;
  searchValue?: string;
  onSearchValueChange?: (value: string) => void;
  hideOptions?: boolean;
  searchInputIcon?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

const InputSelectDropdown: FC<InputSelectDropdownProps> = ({
  dataTestId,
  label,
  options,
  searchPlaceholder,
  noSearchResultsText,
  defaultSelected,
  isHorizontal,
  description,
  className,
  iconClassName,
  contentClassName,
  disabled,
  dropdownAlignment = 'start',
  dropdownSide = 'bottom',
  showCheckMarkOnSelect,
  shouldHighlightSelectedOption,
  shouldCapitalizeOptions = true,
  onOptionChange,
  placeholder = 'Select',
  isRequired = false,
  id,
  buttonClassName,
  buttonDataTestId = '',
  listDataTestId = '',
  descriptionDataTestId = '',
  state = 'default',
  onClear: allowClear,
  clearIconPosition = 'right',
  emptyStateRenderer,
  emptyStateClassName,
  onSearchKeyDown,
  searchValue: controlledSearchValue,
  onSearchValueChange,
  hideOptions,
  searchInputIcon,
  open: controlledOpen,
  onOpenChange
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = (value: boolean) => {
    if (controlledOpen === undefined) {
      setInternalOpen(value);
    }
    onOpenChange?.(value);
  };

  const [internalSearchValue, setInternalSearchValue] = useState('');
  const [selectedOption, setSelectedOption] = useState(
    defaultSelected || { value: '', label: '' }
  );

  const searchValue = controlledSearchValue ?? internalSearchValue;
  const setSearchValue = (value: string) =>
    onSearchValueChange ? onSearchValueChange(value) : setInternalSearchValue(value);

  useEffect(() => {
    if (defaultSelected !== undefined) {
      setSelectedOption(defaultSelected);
    } else {
      // Clear selection when defaultSelected becomes undefined (e.g., after deletion)
      setSelectedOption({ value: '', label: '' });
    }
  }, [defaultSelected]);

  useEffect(() => {
    if (!open && controlledSearchValue) {
      setInternalSearchValue('');
    }
  }, [open, controlledSearchValue]);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    const emptyOption = { value: '', label: '' };
    setSelectedOption(emptyOption);
    if (onOptionChange) {
      onOptionChange('');
    }
    if (allowClear) {
      allowClear();
    }
  };

  return (
    <div
      className={cn(
        'relative flex w-full',
        isHorizontal
          ? label
            ? 'items-top space-x-4'
            : 'items-top space-x-0'
          : 'flex-col',
        className
      )}
    >
      {label && (
        <UILabel
          htmlFor="select"
          className={cn(
            'flex text-p-s font-medium text-base-foreground mb-2',
            isHorizontal ? 'pt-2' : '',
            disabled ? 'text-base-mutedForeground' : 'text-base-foreground'
          )}
          data-testid={dataTestId}
        >
          {isRequired && (
            <div className="mr-2 mt-1">
              <FontAwesomeIcon
                icon="fa-regular fa-asterisk"
                className="h-2 w-2 text-[8px]"
              />
            </div>
          )}
          {label}
        </UILabel>
      )}
      <div className="w-full">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <UIButton
              variant="outline"
              className={cn(
                'shadow-none group h-10 justify-between bg-white p-s text-base-foreground font-normal w-full hover:bg-base-accent focus:bg-base-accent',
                !selectedOption.label &&
                placeholder &&
                'text-base-mutedForeground',
                buttonClassName
              )}
              disabled={disabled}
              id={id}
              data-testid={buttonDataTestId}
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center overflow-hidden text-ellipsis whitespace-nowrap">
                  {allowClear && selectedOption.value && clearIconPosition === 'left' && (
                    <button
                      type="button"
                      onClick={handleClear}
                      className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-base-accent transition-colors mr-2"
                      aria-label="Clear selection"
                    >
                      <FontAwesomeIcon
                        icon="fa-solid fa-xmark"
                        className="text-base-foreground text-xs"
                      />
                    </button>
                  )}
                  {!selectedOption.hideLabel && selectedOption.leftIcon && (
                    <div className="mr-2">{selectedOption.leftIcon}</div>
                  )}
                  {selectedOption.image && (
                    <img
                      src={selectedOption.image}
                      alt={selectedOption.label}
                      className="w-6 h-6 mr-2"
                    />
                  )}
                  {selectedOption.label || placeholder}
                </div>
                <div className="flex items-center gap-2">
                  {allowClear && selectedOption.value && clearIconPosition === 'right' && (
                    <button
                      type="button"
                      onClick={handleClear}
                      className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-base-accent transition-colors"
                      aria-label="Clear selection"
                    >
                      <FontAwesomeIcon
                        icon="fa-solid fa-xmark"
                        className="text-base-foreground text-xs"
                      />
                    </button>
                  )}
                  {!selectedOption.hideLabel && selectedOption.rightIcon ? (
                    selectedOption.rightIcon
                  ) : (
                    <FontAwesomeIcon
                      icon="fa-solid fa-chevron-down"
                      className={cn(
                        'text-base-foreground transition-transform group-data-[state=open]:rotate-180 text-[#404040]',
                        iconClassName
                      )}
                    />
                  )}
                </div>
              </div>
            </UIButton>
          </PopoverTrigger>
          <PopoverContent
            align={dropdownAlignment}
            side={dropdownSide}
            className={cn(
              'w-[--radix-popover-trigger-width] p-0 rounded-md p-s bg-white text-base-foreground font-normal z-1000',
              contentClassName
            )}
          >
            <Command
              filter={(value, search) => {
                if (!search) return 1;
                if (value.toLowerCase().includes(search.toLowerCase())) return 1;
                return 0;
              }}
            >
              {searchPlaceholder && (
                <CommandInput
                  className="p-s text-base-mutedForeground"
                  placeholder={searchPlaceholder}
                  value={searchValue}
                  onValueChange={setSearchValue}
                  onKeyDown={(e) => onSearchKeyDown?.(e, searchValue, () => setSearchValue(''), () => setOpen(false))}
                  icon={searchInputIcon}
                />
              )}
              <CommandList data-testid={listDataTestId}>
                <CommandEmpty className={emptyStateClassName}>
                  {emptyStateRenderer
                    ? emptyStateRenderer(searchValue, () => setSearchValue(''), () => setOpen(false))
                    : noSearchResultsText || 'No results found.'}
                </CommandEmpty>
                {options.length > 0 && !hideOptions && (
                  <CommandGroup>
                    {options.map((option: InputSelectDropdownOption) => (
                      <CommandItem
                        key={option.value}
                        value={option.label}
                        keywords={[option.label]}
                        data-testid={
                          option.dataTestId ||
                          `UploadContent-TypeSelect-${option.value}`
                        }
                        onSelect={() => {
                          setSelectedOption(option);
                          setOpen(false);
                          setSearchValue('');
                          if (onOptionChange)
                            onOptionChange(option.value as string);
                        }}
                        disabled={option.disabled}
                        className={cn(
                          'hover:bg-base-accent rounded-md',
                          shouldCapitalizeOptions && 'capitalize',
                          shouldHighlightSelectedOption &&
                          option.value === selectedOption.value &&
                          'bg-base-accent'
                        )}
                      >
                        <div className="flex items-center justify-between w-full cursor-pointer">
                          <div className="flex items-center flex-1 min-w-0">
                            {option.leftIcon && (
                              <div className="mr-2">{option.leftIcon}</div>
                            )}
                            {option.image && (
                              <img
                                src={option.image}
                                alt={option.label}
                                className="w-6 h-6 mr-2"
                              />
                            )}
                            {!option.hideLabel && <span className="truncate">{option.label}</span>}
                          </div>
                          <div className="flex items-center gap-2 ml-2">
                            {option.rightIcon && <div>{option.rightIcon}</div>}
                            {showCheckMarkOnSelect && (
                              <Check
                                className={cn(
                                  'ml-auto',
                                  selectedOption.value === option.value
                                    ? 'bg-base-foreground'
                                    : 'opacity-0'
                                )}
                              />
                            )}
                          </div>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {description && (
          <div
            data-testid={descriptionDataTestId}
            className={`text-p-s mt-2 ${state === 'error' ? 'text-base-destructive' : 'text-base-mutedForeground'}`}
          >
            {description}
          </div>
        )}
      </div>
    </div>
  );
};

export default InputSelectDropdown;
